import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  Check,
  ChevronDown,
  Handshake,
  Minus,
  ShieldCheck,
  Sparkles,
  Target,
  UserCheck,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";

const fade = {
  hidden: { opacity: 0, y: 22 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.12 },
  },
};

const benchPillars = [
  {
    icon: UserCheck,
    role: "Former CIOs",
    detail:
      "Ran IT for multi-hospital systems — EHR, infrastructure, analytics, and the politics of it.",
  },
  {
    icon: ShieldCheck,
    role: "Former CISOs",
    detail:
      "Led security across clinical and administrative stacks, including incident response at scale.",
  },
  {
    icon: Sparkles,
    role: "Former CTOs",
    detail:
      "Shipped healthcare technology platforms and modernization programs across live environments.",
  },
];

const operatorStats = [
  { value: "150+", label: "Years combined operator experience" },
  { value: "300+", label: "Healthcare programs shipped" },
  { value: "100%", label: "Vendor-independent recommendations" },
];

const comparisonDimensions = [
  { key: "team", label: "Who's on your account" },
  { key: "depth", label: "Healthcare depth" },
  { key: "model", label: "Delivery model" },
  { key: "accountability", label: "Accountability" },
  { key: "engagement", label: "Typical engagement" },
];

const competitors = [
  {
    id: "big4",
    name: "Big 4 Advisory",
    subtitle: "Global consulting & transformation firms",
    highlight: false,
    cells: {
      team: {
        tone: "weak",
        text: "Partners sell, associates deliver",
      },
      depth: {
        tone: "mixed",
        text: "Variable — healthcare is one of many verticals",
      },
      model: {
        tone: "weak",
        text: "Slide-deck driven; execution is someone else's job",
      },
      accountability: {
        tone: "weak",
        text: "Recommendations; no ownership past go-live",
      },
      engagement: {
        tone: "mixed",
        text: "12–24 month transformations with high burn",
      },
    },
  },
  {
    id: "msp",
    name: "Traditional MSPs",
    subtitle: "Regional VARs · Managed service providers",
    highlight: false,
    cells: {
      team: {
        tone: "weak",
        text: "Generalist technicians, ticket-based staffing",
      },
      depth: {
        tone: "weak",
        text: "Healthcare is a workload, not a specialty",
      },
      model: {
        tone: "mixed",
        text: "Ticket-driven, reactive support",
      },
      accountability: {
        tone: "mixed",
        text: "Uptime SLAs, narrow scope of ownership",
      },
      engagement: {
        tone: "mixed",
        text: "Annual managed services contracts",
      },
    },
  },
  {
    id: "velozent",
    name: "Velozent",
    subtitle: "Operator-led healthcare IT partner",
    highlight: true,
    cells: {
      team: {
        tone: "strong",
        text: "Former CIOs, CISOs, CTOs — every engagement",
      },
      depth: {
        tone: "strong",
        text: "Exclusive — 100% of our work is healthcare IT",
      },
      model: {
        tone: "strong",
        text: "Runbook-driven, operator-led through landing",
      },
      accountability: {
        tone: "strong",
        text: "Outcome ownership — measurable, committed in writing",
      },
      engagement: {
        tone: "strong",
        text: "2-week assessments to 12-month outcome programs",
      },
    },
  },
];

const toneIcon = {
  strong: { Icon: Check, cls: "text-complement-600" },
  mixed: { Icon: Minus, cls: "text-shark-400" },
  weak: { Icon: X, cls: "text-shark-400" },
};

const deliveryBeliefs = [
  {
    icon: BookOpen,
    kicker: "Belief · 01",
    title: "Runbooks over slide decks",
    detail:
      "We ship executable playbooks — not recommendations you have to rehire someone to run. The deliverable is operational, not theoretical.",
  },
  {
    icon: UserCheck,
    kicker: "Belief · 02",
    title: "Operators run the first 90 days",
    detail:
      "Senior operators aren't just in kickoff. They're in the room through initial delivery and transition to your team once the work lands.",
  },
  {
    icon: Handshake,
    kicker: "Belief · 03",
    title: "Vendor-independent by design",
    detail:
      "No resell margins. No referral fees. We recommend what works for your environment — not what pays us on the backend.",
  },
  {
    icon: Target,
    kicker: "Belief · 04",
    title: "Outcomes anchor the work",
    detail:
      "Engagements are framed around measurable outcomes — cost, uptime, recovery time, workload eliminated — not hours billed.",
  },
];

const faqs = [
  {
    q: "How are you different from Big 4 advisors and traditional MSPs?",
    a: "Big 4 firms sell transformation slideware delivered by junior consultants. Traditional MSPs run tickets. We're operator-led — the person on your kickoff was a CIO or CISO at a health system, and they stay through the landing. We don't resell software, we don't run T&M hours, and we own outcomes rather than hand them off. If you're buying deliverables that actually run in production, we're the fit.",
  },
  {
    q: "What does a typical engagement look like — size, timeline, pricing?",
    a: "Advisory work ranges from 2-week assessments to 90-day strategy programs. Production programs run 6–12 months with milestone-based phasing. Smaller advisory is typically fixed-fee; larger programs are milestone- and outcome-anchored. We don't bid on pure T&M.",
  },
  {
    q: "Who specifically will be on my account?",
    a: "A named senior operator — former CIO, CISO, or CTO — is assigned to your engagement and listed in the SOW. You aren't renting a staff bench; you're buying decision-making time from someone who has run the job. Delivery teams scale with the work, but the accountable operator stays consistent end-to-end.",
  },
  {
    q: "How do you handle accountability if the work doesn't hit the numbers?",
    a: "We only accept engagements where outcomes can be measured. Those outcomes are committed in writing in the SOW. On advisory work, if we miss them, we continue uncompensated until we hit them. On production programs we carry explicit SLA targets tied to milestones.",
  },
  {
    q: "Are you vendor-independent or do you resell software?",
    a: "Fully vendor-independent. Zero resell margins. Zero referral fees. Every recommendation is based on fit for your environment, not compensation to us. That's a material difference from firms whose P&L depends on moving specific licenses.",
  },
  {
    q: "What happens after the initial engagement ends?",
    a: "Every engagement closes with a transition plan — runbooks, documentation, and operational ownership handed to your team. Ongoing managed services are available but never required. Roughly half of our clients extend; the other half run the playbook themselves. Either is a successful outcome.",
  },
];

function FaqItem({ q, a, open, onToggle }) {
  return (
    <div
      className={`rounded-2xl border transition-colors ${
        open
          ? "border-complement-300 bg-white"
          : "border-shark-200 bg-white hover:border-complement-200"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-start gap-4 px-5 py-5 text-left sm:px-6"
      >
        <span
          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-bold transition-colors ${
            open
              ? "bg-complement-600 text-white"
              : "bg-complement-50 text-complement-700 ring-1 ring-complement-200"
          }`}
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-300 ${
              open ? "rotate-180" : ""
            }`}
            strokeWidth={2.5}
          />
        </span>
        <span className="flex-1 text-[0.98rem] font-bold leading-snug text-shark-900 sm:text-[1.05rem]">
          {q}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pl-[60px] pr-5 sm:px-6 sm:pb-6 sm:pl-[72px]">
              <p className="text-[0.92rem] leading-[1.65] text-shark-600">
                {a}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function WhyVelozent() {
  const reduceMotion = useReducedMotion();
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      <Helmet>
        <title>
          Why Velozent | Operator-Led Healthcare IT — Former CIOs, CISOs, CTOs
        </title>
        <meta
          name="description"
          content="Why health systems choose Velozent: former CIOs, CISOs, and CTOs delivering operator-led healthcare IT with runbooks instead of slide decks, vendor-independent recommendations, and measurable outcomes."
        />
        <meta
          name="keywords"
          content="why Velozent, operator-led healthcare IT, vendor-independent, healthcare CIO, healthcare CISO, healthcare IT partner"
        />
        <meta
          property="og:title"
          content="Why Velozent | Operator-Led Healthcare IT"
        />
        <meta
          property="og:description"
          content="Former CIOs, CISOs, and CTOs delivering outcome-anchored healthcare IT. Runbooks not slide decks. Vendor-independent."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://velozent.com/why-velozent" />
        <link rel="canonical" href="https://velozent.com/why-velozent" />
      </Helmet>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-shark-950 px-4 pb-16 pt-20 text-white lg:pb-20 lg:pt-24">
        <div className="hero-mesh">
          <div className="hero-mesh-orb hero-mesh-orb--1" />
          <div className="hero-mesh-orb hero-mesh-orb--2" />
          <div className="hero-mesh-orb hero-mesh-orb--3" />
          <div className="hero-mesh-grain" />
        </div>

        <div className="relative z-10 mx-auto max-w-5xl">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            className="text-center"
          >
            <motion.p
              variants={fade}
              className="text-[11px] font-bold uppercase tracking-[0.22em] text-burnt-sienna-400"
            >
              Why Velozent
            </motion.p>

            <motion.h1
              variants={fade}
              className="mx-auto mt-4 max-w-4xl text-balance text-[clamp(2rem,4.6vw,3.15rem)] font-extrabold leading-[1.08] tracking-tight text-white"
            >
              The healthcare IT partner that&apos;s{" "}
              <span className="bg-gradient-to-r from-burnt-sienna-300 via-burnt-sienna-400 to-burnt-sienna-500 bg-clip-text text-transparent">
                run the job
              </span>
            </motion.h1>

            <motion.p
              variants={fade}
              className="mx-auto mt-5 max-w-2xl text-[1.05rem] leading-relaxed text-white/75"
            >
              Built by former CIOs, CISOs, and CTOs — not generalists selling
              transformation decks. We ship runbooks, own outcomes, and stay
              through the landing.
            </motion.p>

            <motion.div
              variants={fade}
              className="mx-auto mt-8 flex max-w-3xl flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/60"
            >
              <span>150+ years operator experience</span>
              <span className="h-1 w-1 rounded-full bg-white/30" aria-hidden />
              <span>15 hospitals · 250+ clinics</span>
              <span className="h-1 w-1 rounded-full bg-white/30" aria-hidden />
              <span>100% vendor-independent</span>
            </motion.div>

            <motion.div
              variants={fade}
              className="mt-9 flex flex-wrap items-center justify-center gap-4"
            >
              <Button
                asChild
                size="lg"
                className="group h-12 border-0 px-8 text-[14px] font-bold text-white shadow-[0_8px_36px_-8px_rgba(231,109,56,0.75)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_44px_-8px_rgba(231,109,56,0.55)]"
                style={{ backgroundColor: "#e76d38" }}
              >
                <Link to="/contact">
                  Book Your 15-Minute Strategy Call
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
              <a
                href="#comparison"
                className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white/60 transition-colors hover:text-white"
              >
                See how we compare
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </motion.div>

            <motion.p
              variants={fade}
              className="mt-5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40"
            >
              No obligation · No pitch deck · 15-minute call
            </motion.p>
          </motion.div>
        </div>

        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-burnt-sienna-500/50 to-transparent" />
      </section>

      {/* ── Operator thesis ──────────────────────────────────────────────── */}
      <section className="relative bg-white px-4 py-16 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.25fr] lg:gap-14">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-complement-600">
                The operator thesis
              </p>
              <h2 className="mt-4 max-w-xl text-[clamp(1.625rem,3vw,2.25rem)] font-extrabold leading-[1.12] tracking-tight text-shark-950">
                Why the people behind Velozent change the work
              </h2>
              <div
                className="mt-6 h-px w-10 bg-complement-500/70"
                aria-hidden
              />
              <p className="mt-5 max-w-[520px] text-[0.98rem] leading-[1.65] text-shark-500">
                Velozent was founded and is run by executives who led
                healthcare IT inside health systems — the people accountable
                when EHRs went down, M&amp;A went sideways, or a ransomware
                event hit the front page.
              </p>
              <p className="mt-4 max-w-[520px] text-[0.98rem] leading-[1.65] text-shark-500">
                That background shapes everything: what we build, how we
                staff, how we communicate with boards, and what we&apos;re
                willing to promise in writing.
              </p>

              <div className="mt-8 grid grid-cols-3 gap-3 sm:max-w-md">
                {operatorStats.map((s) => (
                  <div
                    key={s.label}
                    className="rounded-xl border border-shark-200 bg-shark-50/60 px-3 py-4 text-center"
                  >
                    <p className="text-[1.35rem] font-extrabold tracking-tight text-shark-950 sm:text-[1.5rem]">
                      {s.value}
                    </p>
                    <p className="mt-1.5 text-[10px] font-bold uppercase leading-tight tracking-[0.12em] text-shark-500">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <motion.ol
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="space-y-4"
            >
              {benchPillars.map((p, i) => {
                const Icon = p.icon;
                return (
                  <motion.li
                    key={p.role}
                    variants={fade}
                    className="group flex gap-5 rounded-2xl border border-shark-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-complement-300 hover:shadow-[0_22px_64px_-44px_rgba(231,109,56,0.55)] sm:p-6"
                  >
                    <div className="flex flex-col items-center gap-3 sm:w-[84px]">
                      <span className="text-[1.6rem] font-extrabold leading-none text-complement-600">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-complement-50 text-complement-700 ring-1 ring-complement-200">
                        <Icon className="h-4 w-4" strokeWidth={2.2} />
                      </span>
                    </div>

                    <div className="flex-1 border-l border-shark-100 pl-5 sm:pl-6">
                      <h3 className="text-[1.05rem] font-bold text-shark-900 transition-colors group-hover:text-complement-700">
                        {p.role}
                      </h3>
                      <p className="mt-2 text-[0.92rem] leading-relaxed text-shark-500">
                        {p.detail}
                      </p>
                    </div>
                  </motion.li>
                );
              })}
            </motion.ol>
          </div>
        </div>
      </section>

      {/* ── Velozent vs alternatives ────────────────────────────────────── */}
      <section
        id="comparison"
        className="relative bg-shark-50/60 px-4 py-16 lg:px-8 lg:py-20"
      >
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-complement-600">
              How we compare
            </p>
            <h2 className="mt-4 max-w-2xl text-[clamp(1.625rem,3vw,2.25rem)] font-extrabold leading-[1.12] tracking-tight text-shark-950">
              Velozent vs. the alternatives
            </h2>
            <div
              className="mt-6 h-px w-10 bg-complement-500/70"
              aria-hidden
            />
            <p className="mt-5 max-w-[620px] text-[0.98rem] leading-[1.65] text-shark-500">
              Most healthcare IT budgets go to one of three models. Here&apos;s
              how ours differs on the dimensions that matter to a CIO, CFO, or
              board.
            </p>
          </div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3"
          >
            {competitors.map((c) => (
              <motion.div
                key={c.id}
                variants={fade}
                className={`relative flex flex-col rounded-2xl p-6 sm:p-7 ${
                  c.highlight
                    ? "border-2 border-complement-500 bg-white shadow-[0_32px_80px_-44px_rgba(231,109,56,0.5)]"
                    : "border border-shark-200 bg-white"
                }`}
              >
                {c.highlight && (
                  <span className="absolute -top-3 left-6 rounded-full bg-complement-600 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
                    Velozent
                  </span>
                )}

                <div>
                  <h3
                    className={`text-[1.1rem] font-extrabold ${
                      c.highlight ? "text-shark-950" : "text-shark-900"
                    }`}
                  >
                    {c.name}
                  </h3>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-shark-400">
                    {c.subtitle}
                  </p>
                </div>

                <dl className="mt-6 space-y-4 border-t border-shark-100 pt-5">
                  {comparisonDimensions.map((d) => {
                    const cell = c.cells[d.key];
                    const { Icon, cls } = toneIcon[cell.tone];
                    return (
                      <div key={d.key}>
                        <dt className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-shark-400">
                          {d.label}
                        </dt>
                        <dd className="mt-1.5 flex items-start gap-2">
                          <Icon
                            className={`mt-0.5 h-4 w-4 flex-shrink-0 ${cls}`}
                            strokeWidth={2.5}
                          />
                          <span
                            className={`text-[0.88rem] leading-relaxed ${
                              c.highlight ? "text-shark-800" : "text-shark-600"
                            }`}
                          >
                            {cell.text}
                          </span>
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Delivery beliefs ────────────────────────────────────────────── */}
      <section className="relative bg-white px-4 py-16 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-complement-600">
              How we deliver
            </p>
            <h2 className="mt-4 max-w-2xl text-[clamp(1.625rem,3vw,2.25rem)] font-extrabold leading-[1.12] tracking-tight text-shark-950">
              Four beliefs that shape every engagement
            </h2>
            <div
              className="mt-6 h-px w-10 bg-complement-500/70"
              aria-hidden
            />
            <p className="mt-5 max-w-[620px] text-[0.98rem] leading-[1.65] text-shark-500">
              We don&apos;t sell our process as a diagram. We sell it as a set
              of commitments about how the work actually shows up in your
              environment.
            </p>
          </div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2"
          >
            {deliveryBeliefs.map((b) => {
              const Icon = b.icon;
              return (
                <motion.div
                  key={b.title}
                  variants={fade}
                  className="flex gap-5 rounded-2xl border border-shark-200 bg-shark-50/50 p-6 transition-colors hover:border-complement-300 sm:p-7"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-complement-700 ring-1 ring-shark-200">
                    <Icon className="h-5 w-5" strokeWidth={2.2} />
                  </span>
                  <div>
                    <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-shark-400">
                      {b.kicker}
                    </p>
                    <h3 className="mt-1 text-[1.05rem] font-bold text-shark-900">
                      {b.title}
                    </h3>
                    <p className="mt-2 text-[0.92rem] leading-[1.65] text-shark-500">
                      {b.detail}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="relative bg-shark-50/60 px-4 py-16 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-4xl">
          <div className="text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-complement-600">
              Frequently asked
            </p>
            <h2 className="mx-auto mt-4 max-w-2xl text-[clamp(1.625rem,3vw,2.25rem)] font-extrabold leading-[1.12] tracking-tight text-shark-950">
              The questions CIOs actually ask us
            </h2>
            <p className="mx-auto mt-4 max-w-[620px] text-[0.98rem] leading-[1.65] text-shark-500">
              Not the marketing version. The real ones that come up on every
              first call.
            </p>
          </div>

          <div className="mt-12 space-y-3">
            {faqs.map((f, i) => (
              <FaqItem
                key={f.q}
                q={f.q}
                a={f.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-t border-white/10 px-4 py-14 text-center lg:py-20">
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-complement-800 via-complement-950 to-[#070d11]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-[20%] top-1/2 h-[min(500px,55vw)] w-[min(500px,55vw)] -translate-y-1/2 rounded-full bg-complement-500/20 blur-[110px]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-24 left-[10%] h-72 w-72 rounded-full bg-complement-400/12 blur-[80px]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_60%_at_50%_-20%,rgba(255,255,255,0.05),transparent_55%)]"
          aria-hidden
        />

        <motion.div
          className="relative mx-auto max-w-[600px]"
          initial={reduceMotion ? false : { opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-complement-300/75">
            Your next step
          </p>
          <h2 className="mt-3 text-[clamp(1.625rem,3vw,2.25rem)] font-extrabold tracking-tight text-white">
            Talk to an operator who&apos;s run your environment
          </h2>
          <p className="mt-4 text-[1.02rem] leading-relaxed text-white/60">
            Fifteen minutes, no pitch deck. Just a conversation about where
            your IT program is, where it needs to be, and what it would take
            to get there.
          </p>
          <div className="mt-7 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button
              asChild
              size="lg"
              className="h-[54px] border-0 bg-white px-10 text-[16px] font-bold text-complement-950 shadow-lg shadow-black/25 hover:bg-complement-50"
            >
              <Link to="/contact">
                Book Your 15-Minute Strategy Call
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          <p className="mt-4 text-[12px] font-medium text-white/40">
            No obligation. No pitch deck. Just a conversation with an operator.
          </p>
        </motion.div>
      </section>
    </>
  );
}
